import classNames from 'classnames';
import { useRadioCardGroup } from './RadioCard.context';
import type { tRadioCardProps } from './RadioCard.types';
import './RadioCard.styles.scss';

/**
 * A selectable card backed by a real radio input.
 *
 * The input stays in the DOM as `sr-only` rather than being replaced by a
 * div: that keeps the group's native semantics — arrow-key navigation, a
 * single tab stop, screen-reader announcements — while the card provides the
 * visuals. Content is fully composed by the caller.
 *
 * With `placeholder` it renders the same box without a radio, to hold the spot
 * of an option that is still loading.
 *
 * Must be rendered inside a `RadioCardGroup`, which supplies the shared name
 * and the selection.
 */
export default function RadioCard(props: tRadioCardProps) {
  const group = useRadioCardGroup();

  const isSelected = group.value === props.value;
  const isDisabled = group.disabled || Boolean(props.disabled);
  const isAriaDisabled = Boolean(props.ariaDisabled);

  if (props.placeholder) {
    return (
      <div
        className={classNames(
          'RadioCard',
          'RadioCard--disabled',
          { 'RadioCard--equalWidth': group.equalWidth },
          props.className,
        )}
        aria-hidden="true"
        data-testid={props['data-testid']}
      >
        <span className="RadioCard__Mark" />
        <span className="RadioCard__Content invisible">{props.children}</span>
      </div>
    );
  }

  return (
    <label
      className={classNames(
        'RadioCard',
        {
          'RadioCard--selected': isSelected,
          'RadioCard--disabled': isDisabled || isAriaDisabled,
          'RadioCard--invalid': group.invalid,
          'RadioCard--equalWidth': group.equalWidth,
        },
        props.className,
      )}
      data-testid={props['data-testid']}
    >
      <input
        type="radio"
        className="sr-only"
        name={group.name}
        value={props.value}
        checked={isSelected}
        disabled={isDisabled}
        aria-disabled={isAriaDisabled || undefined}
        aria-describedby={props.ariaDescribedBy}
        onChange={() => {
          if (isAriaDisabled) return;
          group.onChange(props.value);
        }}
      />
      <span className="RadioCard__Mark" aria-hidden="true" />
      <span className="RadioCard__Content">{props.children}</span>
    </label>
  );
}
